"use client"

import { useMemo, useState } from "react";
import { Clipboard } from "lucide-react";

import { Textarea } from "@/components/ui/textarea";
import { copyToClipboard } from "@/lib/utils";
import { useTranslations } from "@/components/i18n/use-translations";

const SAMPLE_TOKEN =
    "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJzdWIiOiJ1c2VyLTEyMyIsIm5hbWUiOiJKYW5lIEFuYWx5c3QiLCJpc3MiOiJodHRwczovL2F1dGguZXhhbXBsZS5jb20iLCJpYXQiOjE3MDAwMDAwMDAsImV4cCI6MTcwMDAwMzYwMH0.dGhpcy1pcy1ub3QtYS1yZWFsLXNpZ25hdHVyZQ";

const TIME_CLAIMS = ["exp", "iat", "nbf", "auth_time"];

const decodeSegment = (segment: string) => {
    const base64 = segment.replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
    const binary = atob(padded);
    const bytes = Uint8Array.from(binary, (char) => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes)) as Record<string, unknown>;
};

const decodeToken = (token: string) => {
    const trimmed = token.trim().replace(/^Bearer\s+/i, "");
    if (!trimmed) {
        return null;
    }

    const parts = trimmed.split(".");
    if (parts.length !== 3) {
        return { error: true as const };
    }

    try {
        return {
            error: false as const,
            header: decodeSegment(parts[0]),
            payload: decodeSegment(parts[1]),
            signature: parts[2],
        };
    } catch {
        return { error: true as const };
    }
};

export function JwtDecoder() {
    const [token, setToken] = useState("");
    // Expiry is evaluated against the time the token was entered, captured in event handlers so render stays pure
    const [nowSeconds, setNowSeconds] = useState(0);
    const { messages } = useTranslations();
    const t = messages.tools.jwtDecoder;

    const decoded = useMemo(() => decodeToken(token), [token]);

    const updateToken = (value: string) => {
        setToken(value);
        setNowSeconds(Math.floor(Date.now() / 1000));
    };

    const status = useMemo(() => {
        if (!decoded || decoded.error) {
            return null;
        }
        const exp = typeof decoded.payload.exp === "number" ? decoded.payload.exp : null;
        const nbf = typeof decoded.payload.nbf === "number" ? decoded.payload.nbf : null;
        if (exp !== null && exp < nowSeconds) {
            return { label: t.expired, tone: "text-red-300 border-red-500/40 bg-red-500/10" };
        }
        if (nbf !== null && nbf > nowSeconds) {
            return { label: t.notYetValid, tone: "text-amber-200 border-amber-500/40 bg-amber-500/10" };
        }
        return { label: t.valid, tone: "text-emerald-200 border-emerald-500/40 bg-emerald-500/10" };
    }, [decoded, nowSeconds, t]);

    const algNone = decoded && !decoded.error && String(decoded.header.alg ?? "").toLowerCase() === "none";

    return (
        <div className="mx-auto w-full max-w-4xl space-y-6 px-4 py-8">
            <div className="space-y-4 rounded-2xl border border-slate-800 bg-slate-900/70 p-5 shadow-lg shadow-cyan-500/10">
                <div className="flex flex-col gap-2">
                    <p className="text-xs uppercase tracking-[0.2em] text-cyan-200">{t.label}</p>
                    <h1 className="text-2xl font-semibold text-white">{t.title}</h1>
                    <p className="text-sm text-gray-400">{t.description}</p>
                </div>
                <Textarea
                    value={token}
                    onChange={(event) => updateToken(event.target.value)}
                    placeholder={t.placeholder}
                    aria-label={t.placeholder}
                    spellCheck={false}
                    className="min-h-[120px] border border-slate-800 bg-transparent font-mono text-sm text-slate-100 placeholder:text-gray-500 ring-2 ring-cyan-500/20 focus:ring-cyan-500/45"
                />
                <div className="flex flex-wrap items-center gap-3">
                    <button
                        type="button"
                        onClick={() => updateToken(SAMPLE_TOKEN)}
                        className="rounded-full border border-slate-700 px-3 py-1 text-xs text-gray-300 transition-colors hover:border-cyan-500/60 hover:text-cyan-200"
                    >
                        {t.useSample}
                    </button>
                    <p className="text-xs text-gray-500">{t.unverifiedNote}</p>
                </div>
            </div>

            {decoded?.error && <p className="rounded-xl border border-red-500/40 bg-red-500/10 p-4 text-sm text-red-200">{t.invalid}</p>}

            {decoded && !decoded.error && (
                <div className="space-y-4">
                    <div className="flex flex-wrap gap-2">
                        {status && <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${status.tone}`}>{status.label}</span>}
                        {algNone && (
                            <span className="rounded-full border border-red-500/40 bg-red-500/10 px-3 py-1 text-xs font-semibold text-red-300">{t.algNone}</span>
                        )}
                    </div>

                    <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                        <JsonPanel title={t.header} value={decoded.header} copyLabel={t.copy} />
                        <JsonPanel title={t.payload} value={decoded.payload} copyLabel={t.copy} />
                    </div>

                    {TIME_CLAIMS.some((claim) => typeof decoded.payload[claim] === "number") && (
                        <div className="space-y-2 rounded-xl border border-slate-800/80 bg-slate-950/60 p-4">
                            <h3 className="text-sm font-semibold text-white">{t.claims}</h3>
                            <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
                                {TIME_CLAIMS.filter((claim) => typeof decoded.payload[claim] === "number").map((claim) => (
                                    <div key={claim} className="rounded-lg border border-slate-800/70 bg-slate-900/60 px-3 py-2">
                                        <dt className="font-mono text-xs text-cyan-200">{claim}</dt>
                                        <dd className="text-gray-200">{new Date((decoded.payload[claim] as number) * 1000).toISOString()}</dd>
                                    </div>
                                ))}
                            </dl>
                        </div>
                    )}

                    <div className="space-y-2 rounded-xl border border-slate-800/80 bg-slate-950/60 p-4">
                        <h3 className="text-sm font-semibold text-white">{t.signature}</h3>
                        <p className="break-all font-mono text-xs text-gray-400">{decoded.signature || "-"}</p>
                    </div>
                </div>
            )}
        </div>
    );
}

function JsonPanel({ title, value, copyLabel }: { title: string; value: Record<string, unknown>; copyLabel: string }) {
    const pretty = JSON.stringify(value, null, 2);

    return (
        <div className="space-y-2 rounded-xl border border-slate-800/80 bg-slate-950/60 p-4">
            <div className="flex items-center justify-between">
                <h3 className="text-sm font-semibold text-white">{title}</h3>
                <button
                    type="button"
                    onClick={() => copyToClipboard(pretty)}
                    className="group inline-flex items-center justify-center rounded-lg p-2 text-gray-300 transition-colors hover:bg-slate-700/40 hover:text-cyan-400"
                    aria-label={`${copyLabel} ${title}`}
                >
                    <Clipboard className="h-4 w-4" />
                </button>
            </div>
            <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-all font-mono text-xs text-gray-200">{pretty}</pre>
        </div>
    );
}
